import React, { useState, useMemo } from 'react'
import {
  Heart,
  Pill,
  AlertTriangle,
  Scissors,
  TestTube,
  Users,
  FileText,
  ChevronDown,
  ChevronUp,
  Brain,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  Syringe,
  Plus,
  Building2,
  Hash,
  Factory,
  Trash2,
  ClipboardCheck
} from 'lucide-react'
import './MedicalHistoryTabs.css'

/**
 * MedicalHistoryTabs - A tabbed interface for displaying patient medical history
 *
 * Features:
 * - Dynamic tabs: Only shows tabs that have data
 * - Merges mental health data into Conditions and Medications tabs
 * - Collapsible consultation notes
 * - Badge counts for item quantities
 * - Horizontal scrolling on mobile
 * - Immunizations tab with vaccination upload and compliance check support
 *
 * @param {Object} medicalHistory - The patient's medical_history object
 * @param {Array} consultationNotes - The patient's consultation_notes array (top-level patient field)
 * @param {Object} preventiveCare - The patient's preventive_care object containing vaccinations
 * @param {Function} onAddVaccination - Callback when "Add Vaccination" button is clicked
 * @param {Function} onDeleteVaccination - Callback when a vaccination is deleted (receives vaccination index)
 * @param {Function} onCheckCompliance - Callback when "Check Compliance" button is clicked
 */
function MedicalHistoryTabs({ medicalHistory, consultationNotes, preventiveCare, onAddVaccination, onDeleteVaccination, onCheckCompliance }) {
  const [activeTab, setActiveTab] = useState(null)
  const [expandedNotes, setExpandedNotes] = useState({})

  // Calculate all data and counts
  const tabData = useMemo(() => {
    if (!medicalHistory) return []

    const tabs = []

    // 1. Conditions Tab - merge chronic conditions with mental health conditions, deduplicating
    const chronicConditions = medicalHistory.chronic_conditions || []
    const mentalHealthConditions = medicalHistory.mental_health?.conditions || []

    // Use Map to deduplicate by condition name, prioritizing mental_health type
    const conditionsMap = new Map()

    // Add chronic conditions first
    chronicConditions.forEach(c => {
      conditionsMap.set(c, { name: c, type: 'chronic' })
    })

    // Add mental health conditions, overriding chronic if duplicate
    mentalHealthConditions.forEach(c => {
      conditionsMap.set(c, { name: c, type: 'mental_health' })
    })

    const allConditions = Array.from(conditionsMap.values())
    if (allConditions.length > 0) {
      tabs.push({
        id: 'conditions',
        label: 'Conditions',
        icon: Heart,
        count: allConditions.length,
        data: allConditions
      })
    }

    // 2. Medications Tab - merge all medications with mental health meds
    const currentMedications = medicalHistory.current_medications || []
    const mentalHealthMedications = medicalHistory.mental_health?.medications || []
    const allMedications = [
      ...currentMedications.map(m => ({ ...m, type: 'regular' })),
      ...mentalHealthMedications.map(m => ({ ...m, type: 'mental_health' }))
    ]
    if (allMedications.length > 0) {
      tabs.push({
        id: 'medications',
        label: 'Medications',
        icon: Pill,
        count: allMedications.length,
        data: allMedications
      })
    }

    // 3. Allergies Tab
    const allergies = medicalHistory.allergies || []
    // Filter out "None known" type entries
    const actualAllergies = allergies.filter(a => 
      !a.toLowerCase().includes('none') && a.toLowerCase() !== 'n/a'
    )
    if (actualAllergies.length > 0) {
      tabs.push({
        id: 'allergies',
        label: 'Allergies',
        icon: AlertTriangle,
        count: actualAllergies.length,
        data: actualAllergies
      })
    }

    // 4. Procedures Tab
    const procedures = medicalHistory.past_procedures || []
    if (procedures.length > 0) {
      tabs.push({
        id: 'procedures',
        label: 'Procedures',
        icon: Scissors,
        count: procedures.length,
        data: procedures
      })
    }

    // 5. Labs Tab
    const labs = medicalHistory.blood_test_history || []
    if (labs.length > 0) {
      tabs.push({
        id: 'labs',
        label: 'Labs',
        icon: TestTube,
        count: labs.length,
        data: labs
      })
    }

    // 6. Family History Tab
    const familyHistory = medicalHistory.family_history || []
    if (familyHistory.length > 0) {
      tabs.push({
        id: 'family',
        label: 'Family',
        icon: Users,
        count: familyHistory.length,
        data: familyHistory
      })
    }

    // 7. Consultations Tab - Note: consultation_notes is a top-level patient field, not nested in medical_history
    const consultations = consultationNotes || medicalHistory.consultation_notes || []
    if (consultations.length > 0) {
      tabs.push({
        id: 'consultations',
        label: 'Consultations',
        icon: FileText,
        count: consultations.length,
        data: consultations
      })
    }

    // 8. Immunizations Tab - from preventive_care.vaccinations
    const vaccinations = preventiveCare?.vaccinations || []
    // Always show immunizations tab (even empty) to allow adding new vaccinations
    tabs.push({
      id: 'immunizations',
      label: 'Immunizations',
      icon: Syringe,
      count: vaccinations.length,
      data: vaccinations,
      hasAddButton: true // Flag to show Add button in tab header
    })

    return tabs
  }, [medicalHistory, preventiveCare])

  // Set initial active tab to first available tab
  React.useEffect(() => {
    if (tabData.length > 0 && activeTab === null) {
      setActiveTab(tabData[0].id)
    }
  }, [tabData, activeTab])

  // Toggle consultation note expansion
  const toggleNoteExpansion = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }))
  }

  // Get status color for lab results
  const getLabStatusColor = (status) => {
    if (!status) return 'var(--color-text-secondary)'
    const normalizedStatus = status.toLowerCase()
    if (normalizedStatus === 'normal') return 'var(--color-success)'
    if (normalizedStatus.includes('elevated') || normalizedStatus.includes('high')) return 'var(--color-warning)'
    if (normalizedStatus.includes('low') || normalizedStatus.includes('critical')) return 'var(--color-error)'
    return 'var(--color-text-secondary)'
  }

  // Calculate refill status for medications
  const getRefillStatus = (medication) => {
    if (!medication.pills_remaining || !medication.days_supply) return null
    
    const daysRemaining = medication.pills_remaining / (medication.quantity_prescribed / medication.days_supply)
    
    if (daysRemaining <= 7) {
      return { status: 'critical', label: 'Refill Needed', color: 'var(--color-error)' }
    } else if (daysRemaining <= 14) {
      return { status: 'warning', label: 'Low Supply', color: 'var(--color-warning)' }
    }
    return { status: 'ok', label: `${medication.pills_remaining} pills`, color: 'var(--color-success)' }
  }

  // Render tab content based on active tab
  const renderTabContent = () => {
    const tab = tabData.find(t => t.id === activeTab)
    if (!tab) return null

    switch (tab.id) {
      case 'conditions':
        return (
          <div className="tab-content-conditions">
            {tab.data.map((condition, idx) => (
              <div key={idx} className="condition-item">
                {condition.type === 'mental_health' && (
                  <Brain size={16} className="condition-icon mental-health" aria-label="Mental health condition" />
                )}
                <span className="condition-name">{condition.name}</span>
                {condition.type === 'mental_health' && (
                  <span className="condition-badge mental-health">Mental Health</span>
                )}
              </div>
            ))}
            {/* Display mental health therapy info if available */}
            {medicalHistory.mental_health?.therapy && 
             medicalHistory.mental_health.therapy !== 'None' && (
              <div className="therapy-info">
                <strong>Therapy:</strong> {medicalHistory.mental_health.therapy}
              </div>
            )}
            {medicalHistory.mental_health?.notes && (
              <div className="mental-health-notes">
                <strong>Notes:</strong> {medicalHistory.mental_health.notes}
              </div>
            )}
          </div>
        )

      case 'medications':
        return (
          <div className="tab-content-medications">
            {tab.data.map((med, idx) => {
              const refillStatus = getRefillStatus(med)
              const refillClass = refillStatus?.status === 'critical' ? 'refill-critical' :
                                  refillStatus?.status === 'warning' ? 'refill-warning' : ''
              const pillsPercentage = med.quantity_prescribed && med.pills_remaining
                ? Math.round((med.pills_remaining / med.quantity_prescribed) * 100)
                : null
              
              return (
                <div
                  key={idx}
                  className={`medication-item ${med.type === 'mental_health' ? 'mental-health' : ''} ${refillClass}`}
                >
                  {/* Pill Icon */}
                  <div className="medication-pill-icon">
                    <Pill size={24} />
                  </div>
                  
                  {/* Content */}
                  <div className="medication-content">
                    <div className="medication-header">
                      <div className="medication-name-row">
                        <span className="medication-name">{med.name}</span>
                        {med.type === 'mental_health' && (
                          <span className="medication-badge mental-health">Mental Health</span>
                        )}
                      </div>
                      {refillStatus && (
                        <span className={`refill-status ${refillStatus.status}`}>
                          {refillStatus.status === 'critical' && <AlertCircle size={12} />}
                          {refillStatus.label}
                        </span>
                      )}
                    </div>
                    
                    {/* Dosage & Frequency */}
                    <div className="medication-details">
                      <span className="medication-dosage">{med.dosage}</span>
                      <span className="medication-separator">•</span>
                      <span className="medication-frequency">{med.frequency}</span>
                    </div>
                    
                    {/* Guidelines */}
                    {med.guidelines && (
                      <div className="medication-guidelines">
                        <Info size={14} className="medication-guidelines-icon" />
                        <span className="medication-guidelines-text">{med.guidelines}</span>
                      </div>
                    )}
                    
                    {/* Pills Remaining Progress */}
                    {pillsPercentage !== null && (
                      <div className="medication-pills-remaining">
                        <div className="pills-progress-bar">
                          <div
                            className={`pills-progress-fill ${refillStatus?.status || 'ok'}`}
                            style={{ width: `${pillsPercentage}%` }}
                          />
                        </div>
                        <span className="pills-count">
                          {med.pills_remaining} / {med.quantity_prescribed} pills left
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )

      case 'allergies':
        return (
          <div className="tab-content-allergies">
            {tab.data.map((allergy, idx) => (
              <div key={idx} className="allergy-item">
                <AlertTriangle size={16} className="allergy-icon" />
                <span className="allergy-name">{allergy}</span>
              </div>
            ))}
          </div>
        )

      case 'procedures':
        return (
          <div className="tab-content-procedures">
            {tab.data
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .map((procedure, idx) => (
                <div key={idx} className="procedure-item">
                  <div className="procedure-timeline">
                    <div className="procedure-dot" />
                    {idx < tab.data.length - 1 && <div className="procedure-line" />}
                  </div>
                  <div className="procedure-content">
                    <span className="procedure-name">{procedure.procedure}</span>
                    <span className="procedure-date">
                      <Calendar size={12} />
                      {procedure.date}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        )

      case 'labs':
        return (
          <div className="tab-content-labs">
            <table className="labs-table">
              <thead>
                <tr>
                  <th>Test</th>
                  <th>Value</th>
                  <th>Reference</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {tab.data.map((lab, idx) => (
                  <tr key={idx} className={`lab-row ${lab.status?.toLowerCase()}`}>
                    <td className="lab-test">{lab.test}</td>
                    <td className="lab-value">{lab.value}</td>
                    <td className="lab-reference">{lab.reference}</td>
                    <td className="lab-status" style={{ color: getLabStatusColor(lab.status) }}>
                      {lab.status === 'normal' && <CheckCircle size={14} />}
                      {lab.status?.includes('elevated') && <AlertCircle size={14} />}
                      {lab.status?.includes('critical') && <XCircle size={14} />}
                      {lab.status}
                    </td>
                    <td className="lab-date">{lab.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )

      case 'family':
        return (
          <div className="tab-content-family">
            {tab.data.map((item, idx) => (
              <div key={idx} className="family-item">
                <Users size={16} className="family-icon" />
                <span className="family-text">{item}</span>
              </div>
            ))}
          </div>
        )

      case 'consultations':
        return (
          <div className="tab-content-consultations">
            {tab.data
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .map((note, idx) => {
                const isExpanded = expandedNotes[note.consultation_id]
                return (
                  <div key={note.consultation_id || idx} className="consultation-item">
                    <button
                      className="consultation-header"
                      onClick={() => toggleNoteExpansion(note.consultation_id || idx)}
                      aria-expanded={isExpanded}
                    >
                      <div className="consultation-summary">
                        <span className="consultation-date">{note.date}</span>
                        <span className="consultation-provider">{note.provider}</span>
                        {note.visit_type && (
                          <span className="consultation-type">{note.visit_type}</span>
                        )}
                        {note.chief_complaint && (
                          <span className="consultation-complaint">{note.chief_complaint}</span>
                        )}
                      </div>
                      <span className="consultation-toggle">
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </span>
                    </button>
                    
                    {isExpanded && (
                      <div className="consultation-details">
                        {/* Assessment Section */}
                        {note.assessment && (
                          <div className="consultation-section">
                            <h4>Assessment</h4>
                            {note.assessment.diagnosis && (
                              <p><strong>Diagnosis:</strong> {note.assessment.diagnosis}</p>
                            )}
                            {note.assessment.likely_diagnosis && (
                              <p><strong>Likely Diagnosis:</strong> {note.assessment.likely_diagnosis}</p>
                            )}
                            {note.assessment.clinical_notes && (
                              <p><strong>Notes:</strong> {note.assessment.clinical_notes}</p>
                            )}
                            {note.assessment.overview && (
                              <p>{note.assessment.overview}</p>
                            )}
                            {note.assessment.key_findings && (
                              <div>
                                <strong>Key Findings:</strong>
                                <ul>
                                  {note.assessment.key_findings.map((finding, i) => (
                                    <li key={i}>{finding}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {note.assessment.impression && Array.isArray(note.assessment.impression) && (
                              <div>
                                <strong>Impression:</strong>
                                <ul>
                                  {note.assessment.impression.map((item, i) => (
                                    <li key={i}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Diagnostic Plan Section */}
                        {note.diagnostic_plan && Array.isArray(note.diagnostic_plan) && (
                          <div className="consultation-section">
                            <h4>Diagnostic Plan</h4>
                            <ul className="diagnostic-list">
                              {note.diagnostic_plan.map((item, i) => (
                                <li key={i} className={`diagnostic-item ${item.status}`}>
                                  <span className="diagnostic-name">
                                    {item.item || item.test}
                                  </span>
                                  <span className={`diagnostic-status ${item.status}`}>
                                    {item.status}
                                  </span>
                                  {item.notes && (
                                    <span className="diagnostic-notes">{item.notes}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Treatment Plan Section */}
                        {note.treatment_plan && (
                          <div className="consultation-section">
                            <h4>Treatment Plan</h4>
                            {note.treatment_plan.medication && (
                              <div className="treatment-medication">
                                <strong>Medication:</strong> {note.treatment_plan.medication.name}
                                ({note.treatment_plan.medication.dosing || note.treatment_plan.medication.dosage})
                                {note.treatment_plan.medication.duration && (
                                  <span> - {note.treatment_plan.medication.duration}</span>
                                )}
                              </div>
                            )}
                            {note.treatment_plan.medications && Array.isArray(note.treatment_plan.medications) && (
                              <div className="treatment-medications">
                                <strong>Medications:</strong>
                                <ul>
                                  {note.treatment_plan.medications.map((med, i) => (
                                    <li key={i}>
                                      {med.name} {med.dosage} - {med.frequency}
                                      {med.duration && ` (${med.duration})`}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {note.treatment_plan.monitoring && (
                              <p><strong>Monitoring:</strong> {note.treatment_plan.monitoring}</p>
                            )}
                            {note.treatment_plan.monitoring_requirements && (
                              <p><strong>Monitoring:</strong> {note.treatment_plan.monitoring_requirements}</p>
                            )}
                            {note.treatment_plan.next_steps && Array.isArray(note.treatment_plan.next_steps) && (
                              <div>
                                <strong>Next Steps:</strong>
                                <ul>
                                  {note.treatment_plan.next_steps.map((step, i) => (
                                    <li key={i}>
                                      {typeof step === 'string' ? step : step.action}
                                      {step.timeline && ` (${step.timeline})`}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Detailed Findings Section (for MRI/imaging reports) */}
                        {note.detailed_findings && (
                          <div className="consultation-section detailed-findings">
                            <h4>Detailed Findings</h4>
                            {Object.entries(note.detailed_findings).map(([key, value]) => (
                              <div key={key} className="finding-item">
                                <strong>{key.replace(/_/g, ' ')}:</strong>
                                <p>{value}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Recommendations */}
                        {note.recommendations && (
                          <div className="consultation-section">
                            <h4>Recommendations</h4>
                            <p>{note.recommendations}</p>
                          </div>
                        )}

                        {/* Follow-up */}
                        {note.follow_up && (
                          <div className="consultation-section">
                            <h4>Follow-up</h4>
                            {note.follow_up.scheduled_date && (
                              <p><strong>Scheduled:</strong> {note.follow_up.scheduled_date}</p>
                            )}
                            {note.follow_up.instructions && (
                              <p><strong>Instructions:</strong> {note.follow_up.instructions}</p>
                            )}
                          </div>
                        )}

                        {/* Provider Message */}
                        {note.provider_message && (
                          <div className="consultation-section provider-message">
                            <h4>Provider Message</h4>
                            <p>{note.provider_message}</p>
                          </div>
                        )}

                        {/* Simple plan/notes for TCM calls */}
                        {note.plan && (
                          <div className="consultation-section">
                            <h4>Plan</h4>
                            <p>{note.plan}</p>
                          </div>
                        )}

                        {/* Psychosocial Notes */}
                        {note.psychosocial_notes && (
                          <div className="consultation-section">
                            <h4>Psychosocial Notes</h4>
                            <p>{note.psychosocial_notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        )

      case 'immunizations':
        // Sort vaccinations: primary by vaccine_type (alphabetical), secondary by date (newest first)
        const sortedVaccinations = [...tab.data].sort((a, b) => {
          // Primary sort: vaccine_type alphabetically
          const typeA = (a.vaccine_type || '').toLowerCase()
          const typeB = (b.vaccine_type || '').toLowerCase()
          if (typeA < typeB) return -1
          if (typeA > typeB) return 1
          // Secondary sort: date_administered descending (newest first)
          return new Date(b.date_administered) - new Date(a.date_administered)
        })

        // Handle delete with confirmation
        const handleDeleteVaccination = (vaccination, originalIndex) => {
          if (onDeleteVaccination) {
            const confirmMsg = `Are you sure you want to delete this vaccination record?\n\n${vaccination.vaccine_type || 'Unknown vaccine'}${vaccination.dose_label ? ` - ${vaccination.dose_label}` : ''}\nDate: ${formatDate(vaccination.date_administered)}`
            if (window.confirm(confirmMsg)) {
              onDeleteVaccination(originalIndex)
            }
          }
        }

        return (
          <div className="tab-content-immunizations">
            {/* Action Buttons Header */}
            {(onAddVaccination || onCheckCompliance) && (
              <div className="immunizations-header">
                {onAddVaccination && (
                  <button
                    className="btn-add-vaccination"
                    onClick={onAddVaccination}
                    aria-label="Add vaccination record"
                  >
                    <Plus size={16} />
                    Add Vaccination
                  </button>
                )}
                {onCheckCompliance && (
                  <button
                    className="btn-check-compliance"
                    onClick={onCheckCompliance}
                    aria-label="Check vaccination compliance"
                    disabled={tab.data.length === 0}
                    title={tab.data.length === 0 ? 'Add vaccination records before checking compliance' : 'Check compliance against requirement sets'}
                  >
                    <ClipboardCheck size={16} />
                    Check Compliance
                  </button>
                )}
              </div>
            )}
            
            {tab.data.length === 0 ? (
              <div className="immunizations-empty">
                <Syringe size={48} className="empty-icon" />
                <p>No vaccination records available.</p>
                {onAddVaccination && (
                  <p className="empty-hint">Click "Add Vaccination" to upload a vaccination card.</p>
                )}
              </div>
            ) : (
              <div className="immunizations-grid">
                {sortedVaccinations.map((vaccination, sortedIdx) => {
                  // Find the original index in the unsorted array for deletion
                  const originalIndex = tab.data.findIndex(v =>
                    v.vaccine_type === vaccination.vaccine_type &&
                    v.date_administered === vaccination.date_administered &&
                    v.lot_number === vaccination.lot_number
                  )
                  
                  return (
                    <div key={sortedIdx} className="vaccination-card">
                      <div className="vaccination-card-header">
                        <div className="vaccination-type-badge">
                          <Syringe size={14} />
                          {vaccination.vaccine_type || 'Unknown'}
                        </div>
                        {vaccination.source && (
                          <div className="vaccination-source">
                            {vaccination.source === 'vision_upload' ? '📷 Uploaded' : '✏️ Manual Entry'}
                          </div>
                        )}
                      </div>
                      
                      <div className="vaccination-card-body">
                        {/* Date administered - primary info */}
                        <div className="vaccination-date">
                          <Calendar size={14} />
                          <span>{formatDate(vaccination.date_administered)}</span>
                        </div>
                        
                        {/* Dose label - prominently displayed */}
                        {vaccination.dose_label && (
                          <div className="vaccination-dose-display">
                            <span className="dose-label-text">{vaccination.dose_label}</span>
                          </div>
                        )}
                        
                        {vaccination.manufacturer && (
                          <div className="vaccination-detail">
                            <Factory size={14} />
                            <span>{vaccination.manufacturer}</span>
                          </div>
                        )}
                        
                        {vaccination.lot_number && (
                          <div className="vaccination-detail">
                            <Hash size={14} />
                            <span>Lot: {vaccination.lot_number}</span>
                          </div>
                        )}
                        
                        {vaccination.healthcare_site && (
                          <div className="vaccination-detail">
                            <Building2 size={14} />
                            <span>{vaccination.healthcare_site}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Card footer with next due and delete button */}
                      <div className="vaccination-card-footer">
                        {vaccination.next_due ? (
                          <div className="vaccination-next-due">
                            <span className="next-due-label">Next Due:</span>
                            <span className="next-due-date">{formatDate(vaccination.next_due)}</span>
                          </div>
                        ) : (
                          <div className="vaccination-footer-spacer" />
                        )}
                        
                        {onDeleteVaccination && (
                          <button
                            className="btn-delete-vaccination"
                            onClick={() => handleDeleteVaccination(vaccination, originalIndex)}
                            aria-label={`Delete ${vaccination.vaccine_type || 'vaccination'} record`}
                            title="Delete vaccination record"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )

      default:
        return null
    }
  }

  // Helper function to format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown'
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return dateStr
    }
  }

  // If no medical history data, show empty state (but still allow immunizations tab)
  if (!medicalHistory || tabData.length === 0) {
    return (
      <div className="medical-history-tabs empty">
        <h2>Medical History</h2>
        <p className="empty-message">No medical history data available for this patient.</p>
      </div>
    )
  }

  return (
    <div className="medical-history-tabs">
      <h2>Medical History</h2>
      
      {/* Tab Navigation */}
      <div className="tabs-navigation" role="tablist" aria-label="Medical history categories">
        {tabData.map(tab => {
          const IconComponent = tab.icon
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <IconComponent size={16} className="tab-icon" />
              <span className="tab-label">{tab.label}</span>
              <span className="tab-badge">{tab.count}</span>
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <div 
        className="tabs-content"
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={activeTab}
      >
        {renderTabContent()}
      </div>
    </div>
  )
}

export default MedicalHistoryTabs